import { useEffect, useState } from "react";
import { Input } from "../../../ui";
import { ValidateCIF, ValidateDNI, ValidateNIE, isValidPhone, isValidEmail, isValidPostalCode } from "../../../validation/validations";
import { FormErrors, Datos1Props } from "@/types";
import SelectDniNie from "../validacionesFormularioPaso1/SelectDniNie";
import { OcultarDatosCss, InputCss } from "../../../styles";

const Datos1: React.FC<Datos1Props> = ({
  formData1,
  setFormData1,
  isEmpresa,
  isFirmaAccepted,
  setErrorsDatos1,
  setHasErrorsDatos1,
  readOnly = false,
}) => {
  const [errors, setErrors] = useState<FormErrors>({});
  const value = formData1?.idSelectDniNie;
  // ✅ se mostrará si el flag viene a true o si ya hay datos de empresa cargados
  const esEmpresa =
    isEmpresa || !!formData1?.cif?.trim() || !!formData1?.nombre_empresa?.trim();


  useEffect(() => {
    const hasErrors = Object.values(errors).some((error) => error);
    setErrorsDatos1!(errors);
    setHasErrorsDatos1!(hasErrors);
  }, [errors]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let sanitizedValue = value.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s@.]/g, "");

    if (["cif", "dniNie"].includes(name)) {
      sanitizedValue = sanitizedValue.toUpperCase();
    }

    if (name === "numero_telefono") {
      sanitizedValue = value.replace(/\D/g, "").slice(0, 9);
    }

    setFormData1!((prev) => ({
      ...prev,
      [name]: sanitizedValue,
    }));
  };

  const handleSelectChange = (selectedType: string) => {
    setFormData1!((prev) => ({ ...prev, idSelectDniNie: selectedType, dniNie: "" }));
    setErrors((prev) => ({ ...prev, dniNie: true }));
  };

  const handleDniNieValidation = () => {
    if (!formData1) return;

    const { idSelectDniNie, dniNie } = formData1;

    if (!dniNie || dniNie.trim() === "") {
      setErrors((prev) => ({ ...prev, dniNie: true }));
      return;
    }

    let isValid = false;

    if (idSelectDniNie === "dni") {
      isValid = ValidateDNI(dniNie);
    } else if (idSelectDniNie === "nie") {
      isValid = ValidateNIE(dniNie);
    }

    setErrors((prev) => ({ ...prev, dniNie: !isValid }));
  };

  const handleMailValidation = () => {
    if (formData1!.email!.length > 0) {
      const isValid = isValidEmail(formData1!.email!);
      setErrors((prev) => ({ ...prev, email: !isValid }));

      if (!isValid) {
        setFormData1!((prev) => ({ ...prev, email: "" }));
      }
    }
  };

  const handleCifValidation = () => {
    const isValid = ValidateCIF(formData1?.cif ?? "");
    setErrors((prev) => ({ ...prev, cif: !isValid }));

    if (!isValid) {
      setFormData1!((prev) => ({ ...prev, cif: "" }));
    }
  };

  const handlePhoneValidation = () => {
    const phone = formData1?.numero_telefono?.trim() ?? "";
    const isValid = isValidPhone(phone);
    setErrors((prev) => ({ ...prev, numero_telefono: !isValid }));

    if (!isValid) {
      setFormData1!((prev) => ({ ...prev, numero_telefono: "" }));
    }
  };

  const handlePostalCodeValidation = () => {
    const isValid = isValidPostalCode(formData1?.postalCode ?? "");
    setErrors((prev) => ({ ...prev, postalCode: !isValid }));

    if (!isValid) {
      setFormData1!((prev) => ({ ...prev, postalCode: "" }));
    }
  };

  return (
    <div className={`datos1 ${isFirmaAccepted ? OcultarDatosCss.niebla : ""} grid col-span-12 px-3 pb-3 py-1 space-y-2 rounded-md border dark:bg-white dark:text-zinc-700 gap-4 ${InputCss.inputEdita}`}>
      <label className="font-bold text-sm md:col-span-12 lg:col-span-6 underline">
        Datos Profesional:
      </label>

      <Input
        type="text"
        id="nombre"
        name="nombre"
        value={formData1?.nombre}
        onChange={handleChange}
        placeholder="Nombre..."
        required
        error={errors.nombre}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        type="text"
        id="apellidos"
        name="apellidos"
        value={formData1?.apellidos}
        onChange={handleChange}
        placeholder="Apellidos"
        required
        error={errors.apellidos}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        name="email"
        placeholder={errors.email ? "Correo electrónico inválido" : "Correo electrónico"}
        type="email"
        value={formData1?.email}
        onChange={handleChange}
        onBlur={handleMailValidation}
        required
        error={errors.email}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        type="text"
        name="numero_telefono"
        placeholder={errors.numero_telefono ? "Teléfono inválido" : "Teléfono"}
        value={formData1?.numero_telefono}
        onChange={(e) => setFormData1?.({ ...formData1, numero_telefono: e.target.value })}
        onBlur={handlePhoneValidation}
        error={errors.numero_telefono}
        required
        maxLength={9}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      {!readOnly && (
        <SelectDniNie
          id="idSelectDniNie"
          value={value || ''}
          name="idSelectDniNie"
          onChange={handleSelectChange}
          opciones={['DNI', 'NIE']}
          error={!!errors.dniNie}
          placeholder="DNI / NIE"
        />
      )}

      <Input
        name="dniNie"
        placeholder={errors.dniNie ? "DNI/NIE inválido" : "DNI/NIE"}
        value={formData1?.dniNie}
        onChange={handleChange}
        onBlur={handleDniNieValidation}
        required
        error={errors.dniNie}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        type="text"
        id="calle"
        name="calle"
        value={formData1?.calle}
        onChange={handleChange}
        placeholder="Dirección..."
        required
        error={errors.calle}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        type="text"
        id="locality"
        name="locality"
        value={formData1?.locality}
        onChange={handleChange}
        placeholder="Localidad..."
        required
        error={errors.locality}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        type="text"
        id="province"
        name="province"
        value={formData1?.province}
        onChange={handleChange}
        placeholder="Provincia..."
        required
        error={errors.province}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      <Input
        name="postalCode"
        placeholder={errors.postalCode ? "Código Postal inválido" : "Código Postal"}
        value={formData1?.postalCode}
        onChange={handleChange}
        onBlur={handlePostalCodeValidation}
        error={errors.postalCode}
        required
        maxLength={5}
        readOnly={readOnly} //Aqui se bloquea para no lo edite cuando renueve suscripción
      />

      {(esEmpresa === true) && (

        <>
          <Input
            type="text"
            id="nombre_empresa"
            name="nombre_empresa"
            value={formData1?.nombre_empresa}
            onChange={handleChange}
            placeholder="Nombre de la empresa..."
            required
            error={errors.nombre_empresa}
            readOnly={readOnly}
          />

          <Input
            name="cif"
            placeholder={errors.cif ? "CIF inválido" : "CIF"}
            value={formData1?.cif}
            onChange={handleChange}
            onBlur={handleCifValidation}
            required
            error={errors.cif}
            readOnly={readOnly}
          />
        </>
      )}

    </div>
  );
};

export default Datos1;
